import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  faCircleNotch,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import {
  ClientCaseModel,
  ClientSurveyContactModel,
  ClientSurveyTypeModel,
} from '../../../api/models';
import {
  SurveyContactService,
  ValidationTokenService,
} from '../../../api/services';
import { BaseAccountInfoComponent } from '../../../components/BaseAccountInfo/BaseAccountInfo.component';
import { ContactHistoryGridComponent } from '../../../components/grids/contact-history-grid/contact-history-grid.component';
import { SurveyHistoryGridComponent } from '../../../components/grids/survey-history-grid/survey-history-grid.component';
import { AddContactHistoryModalComponent } from '../../../components/modals/add-contact-history-modal/add-contact-history-modal.component';
import { TakeSurveyModalComponent } from '../../../components/modals/take-survey-modal/take-survey-modal.component';
import { QueryParamsService } from '../../../services/query-params.service';
import { ToastService } from '../../../services/toast.service';
import { CaseService } from './../../../api/services/case.service';
import { SurveyTypeService } from './../../../api/services/survey-type.service';
import { AccountInfoService } from './../../../services/account-info.service';

@Component({
  selector: 'app-matter-detail',
  templateUrl: './matter-detail.component.html',
  styleUrls: ['./matter-detail.component.scss'],
})
export class MatterDetailComponent
  extends BaseAccountInfoComponent
  implements OnInit
{
  @ViewChild(AddContactHistoryModalComponent)
  addContactHistoryModalComponentChild!: AddContactHistoryModalComponent;

  @ViewChild(TakeSurveyModalComponent)
  takeSurveyModalComponentChild!: TakeSurveyModalComponent;

  @ViewChild(SurveyHistoryGridComponent)
  surveyHistoryGridComponentChild!: SurveyHistoryGridComponent;

  @ViewChild(ContactHistoryGridComponent)
  contactHistoryGridComponentChild!: ContactHistoryGridComponent;

  clientsToCallQueryParams: { [key: string]: string };

  matterId: number;
  surveyTypeId: number;

  // Auth stuff
  isAuthLoading = true;
  surveyContact: ClientSurveyContactModel;
  matterHasRepAssigned: boolean = false;

  isSurveyTypeDetailsLoading = true;
  isMatterLoading = true;
  isEmailSending = false;

  isSurveyHistoryLoading = true;

  surveyTypeDetails: ClientSurveyTypeModel;
  matterDetails: ClientCaseModel;

  month: number;
  year: number;

  faCircleNotch = faCircleNotch;
  faTriangleExclamation = faTriangleExclamation;

  // Note form stuff.
  addContactNotesForm: FormGroup;
  isSubmitButtonEnabled: boolean = false;
  originalNotesValue: string = '';

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private toastService: ToastService,
    private caseService: CaseService,
    private surveyTypeService: SurveyTypeService,
    protected override accountInfoService: AccountInfoService,
    private surveyContactService: SurveyContactService,
    private validationTokenService: ValidationTokenService,
    private queryParamsService: QueryParamsService
  ) {
    super(accountInfoService);
  }

  override ngOnInit() {
    super.ngOnInit();

    this.clientsToCallQueryParams = this.queryParamsService.getQueryParams(); // Retrieve from service so we can add them to the Clients to Call link.

    this.addContactNotesForm = this.fb.group({
      notes: [''],
    });

    this.matterId = parseInt(this.route.snapshot.paramMap.get('matterId')!);
    this.surveyTypeId = parseInt(
      this.route.snapshot.paramMap.get('surveyTypeId')!
    );
    this.month = parseInt(this.route.snapshot.paramMap.get('month')!);
    this.year = parseInt(this.route.snapshot.paramMap.get('year')!);
    this.getSurveyTypeDetails();
    this.getMatterDetails();
  }

  getSurveyTypeDetails() {
    this.isSurveyTypeDetailsLoading = true;

    this.surveyTypeService
      .getSurveyType$Json$Response({ surveyTypeId: this.surveyTypeId })
      .subscribe({
        next: (r) => {
          this.isSurveyTypeDetailsLoading = false;
          if (!r.body) {
            console.error(r.body);
            this.toastService.showToast(
              'danger',
              'Problem loading survey type details'
            );
          } else {
            this.surveyTypeDetails = r.body;
          }
        },
        error: (error) => {
          console.error(error, this);
          this.isSurveyTypeDetailsLoading = false;
          this.toastService.showToast(
            'danger',
            'Problem loading survey type details'
          );
        },
      });
  }

  getMatterDetails() {
    this.isMatterLoading = true;

    this.caseService
      .getClientCase$Json$Response({
        caseId: this.matterId,
        surveyTypeId: this.surveyTypeId,
      })
      .subscribe({
        next: (r) => {
          this.isMatterLoading = false;
          if (!r.body) {
            console.error(r.body);
            this.toastService.showToast(
              'danger',
              'Problem loading matter details'
            );
          } else {
            this.matterDetails = r.body;

            // Make sure they have a rep id.
            if (
              this.accountInfo!.repId == null ||
              this.accountInfo!.repId == undefined
            ) {
              // Redirect to the access-denied page.
              this.router.parseUrl('/access-denied');
            }
          }
        },
        error: (error) => {
          console.error(error, this);
          this.isMatterLoading = false;
          this.toastService.showToast(
            'danger',
            'Problem loading matter details'
          );
        },
      });
  }

  setSurveyContact(value: ClientSurveyContactModel) {
    this.surveyContact = value;

    if (value) {
      if (value.clientRepId == null || value.clientRepId == undefined) {
        this.matterHasRepAssigned = false;
      } else {
        this.matterHasRepAssigned = true;
      }
      // Set the notes form value.
      this.addContactNotesForm.patchValue({ notes: value.note });
      // Keep track of the original value so we know when to enable the save button.
      this.originalNotesValue = this.addContactNotesForm.get('notes')!.value;

      // Subscribe to changes. If the value changes, enable the save button.
      this.addContactNotesForm.get('notes')!.valueChanges.subscribe((value) => {
        this.isSubmitButtonEnabled = value !== this.originalNotesValue;
      });
    }
    if (!value && this.matterHasRepAssigned === false) {
      this.matterHasRepAssigned = false;
    }

    this.isAuthLoading = false;
  }

  onContactNotesSubmit() {
    // Update
    this.surveyContactService
      .updateSurveyContactAsync$Json$Response({
        surveyContactId: this.surveyContact.surveyContactId!.toString(),
        body: {
          clientRepId: this.surveyContact.clientRepId!,
          note: this.addContactNotesForm.value.notes,
          surveyContactId: this.surveyContact.surveyContactId!,
          month: this.month,
          year: this.year,
        },
      })
      .subscribe({
        next: (r) => {
          if (!r.body) {
            this.toastService.showToast('danger', 'Problem saving note');
          } else {
            this.toastService.showToast('success', 'Note saved successfully');
          }
        },
        error: (error) => {
          console.error(error, this);
          this.toastService.showToast('danger', 'Problem saving note');
        },
      });
  }

  executeSendSurveyProcess() {
    this.isEmailSending = true;
    // 1. Get validation token.
    const validationTokenRes$ = this.createValidationToken();
    // 2. Send survey.
    // 3. Create client contact record.

    validationTokenRes$.subscribe({
      next: (r) => {
        if (r.body) {
          if (r.body.token) {
            this.sendSurvey(r.body.token);
          } else {
            this.isEmailSending = false;
            console.error(r.body);
            this.toastService.showToast(
              'danger',
              'Problem creating validation token'
            );
          }
        } else {
          this.isEmailSending = false;
          console.error(r.body);
          this.toastService.showToast(
            'danger',
            'Problem creating validation token'
          );
        }
      },
      error: (error) => {
        this.isEmailSending = false;
        console.error(error, this);
        this.toastService.showToast(
          'danger',
          'Problem creating validation token'
        );
      },
    });
  }

  createValidationToken() {
    return this.validationTokenService.createValidationToken$Json$Response({
      body: {
        caseId: this.matterId,
        surveyTypeId: this.surveyTypeId,
        month: this.month,
        year: this.year,
      },
    });
  }

  sendSurvey(token: string) {
    const clientRepId =
      this.contactHistoryGridComponentChild.surveyContact.clientRepId;
    if (clientRepId !== undefined) {
      this.caseService
        .sendSurvey$Json$Response({
          caseId: this.matterId.toString(),
          body: {
            clientRepId: clientRepId,
            validationToken: token,
            caseId: this.matterId,
            surveyTypeId: this.surveyTypeId,
          },
        })
        .subscribe({
          next: (r) => {
            if (!r.body) {
              this.toastService.showToast('danger', 'Problem sending survey');
            } else if (r.body.isError === true) {
              this.toastService.showToast(
                'danger',
                `Problem sending survey: ${r.body.message}`
              );
            } else {
              this.updateSurveyContact(true, false);
              this.toastService.showToast(
                'success',
                'Survey sent successfully'
              );
            }
            this.isEmailSending = false;
          },
          error: (error) => {
            this.isEmailSending = false;
            console.error(error, this);
            this.isSurveyHistoryLoading = false;
            this.toastService.showToast('danger', 'Problem sending survey');
          },
        });
    } else {
      this.isEmailSending = false;
      this.isSurveyHistoryLoading = false;
      this.toastService.showToast('danger', 'No clientRepId found');
    }
  }

  updateSurveyContact(isForSurveySend = false, isForTakenSurvey = false) {
    this.addContactHistoryModalComponentChild.getSurveyContact().subscribe({
      next: (r) => {
        if (!r.body) {
          this.addContactHistoryModalComponentChild.hasSurveyContact = false;
        } else {
          this.addContactHistoryModalComponentChild.hasSurveyContact = true;
          this.addContactHistoryModalComponentChild.surveyContact = r.body;
        }
        this.addContactHistoryModalComponentChild.submitContactRecord(
          isForSurveySend,
          isForTakenSurvey
        );
      },
      error: (error) => {
        console.error(error, this);
        this.toastService.showToast('danger', 'Problem loading survey contact');
        this.addContactHistoryModalComponentChild.hasSurveyContact = false;
      },
    });
  }

  toggleAddContactHistoryModal() {
    this.addContactHistoryModalComponentChild.toggleAddContactHistoryModal();
  }

  toggleTakeSurveyModal() {
    this.takeSurveyModalComponentChild.toggleTakeSurveyModal();
  }

  reloadSurveyContact() {
    this.contactHistoryGridComponentChild.getSurveyContact();
  }
  reloadSurveyHistories() {
    this.contactHistoryGridComponentChild.getSurveyContact();
    this.surveyHistoryGridComponentChild.loadSurveyHistory();
  }
}
